import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.js';
import { invalidateRequestCaches } from './invalidateRequestCaches.js';
import { getRealtimeSocket } from './socket.js';

const DEBOUNCE_MS = 750;

/**
 * @aiPurpose browser-lifecycle catch-up for all realtime-backed domains
 *   (#517). Socket.IO `reconnect` only fires when the client itself
 *   observed a transport drop and recovered, but browsers and OSes can
 *   freeze tabs, throttle timers, discard pages, restore from bfcache,
 *   or break sockets while the user is away — states the Manager-level
 *   `reconnect` does not always cover. On user return (visibility,
 *   focus, online, bfcache `pageshow`, or post-discard restoration)
 *   this hook reconciles the realtime-backed domains through REST-backed
 *   invalidation so the user is not looking at stale state without a
 *   click.
 * @aiInvariant catch-up is intentionally local to realtime-backed query
 *   slices even though `apps/web/src/queryClient.ts` keeps
 *   `refetchOnWindowFocus: false` globally. Enabling the global TanStack flag would also
 *   refetch users/audit/etc., which are pull-by-mutation and do not
 *   need it. Future cleanup MUST NOT "consolidate" by enabling the
 *   global flag — the asymmetry is the point.
 * @aiInvariant the debounced fire reads `useAuthStore.getState().accessToken`
 *   at execution time so a session cleared during the debounce window
 *   does not produce a connect or invalidate against a logged-out
 *   identity. The captured `accessToken` from the effect closure is
 *   sufficient for gating listener attachment but stale by the time
 *   the timer fires.
 */
export function useLifecycleCatchUp(accessToken: string | null, queryClient: QueryClient): void {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!accessToken) return;

    const runCatchUp = () => {
      debounceRef.current = null;
      const latestToken = useAuthStore.getState().accessToken;
      if (!latestToken) return;
      const socket = getRealtimeSocket();
      if (!socket.connected) socket.connect();
      invalidateRequestCaches(queryClient);
    };

    const scheduleCatchUp = () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(runCatchUp, DEBOUNCE_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleCatchUp();
    };
    const onFocus = () => scheduleCatchUp();
    const onOnline = () => scheduleCatchUp();
    const onPageshow = (event: PageTransitionEvent) => {
      // INVARIANT: cold loads / F5 also fire `pageshow` with `persisted: false`.
      //   Catching up there would duplicate the fresh queries `useQuery` already
      //   issues on mount. Only the bfcache restore path (`persisted: true`)
      //   carries stale state worth reconciling here.
      if (event.persisted) scheduleCatchUp();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageshow);

    // INVARIANT: `document.wasDiscarded` is the Page Lifecycle signal that
    //   the browser threw the page away to reclaim memory and reloaded it
    //   on user return. The property is not in `lib.dom.d.ts` (Page
    //   Lifecycle is still a draft), so feature-detect via narrowed cast.
    //   One catch-up at mount is sufficient — subsequent lifecycle events
    //   reuse the same scheduler.
    const docWithDiscarded = document as Document & { wasDiscarded?: boolean };
    if (docWithDiscarded.wasDiscarded === true) {
      scheduleCatchUp();
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageshow);
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [accessToken, queryClient]);
}
